import React, { lazy, Suspense, useEffect } from 'react';
import { Box, Button, Spinner, Text, VStack } from '@chakra-ui/react';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation, useParams } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import AppUpdateNotice from './components/AppUpdateNotice';
import RequireAuth from './components/RequireAuth';
import RequireWorkspaceAccess from './components/RequireWorkspaceAccess';
import WorkspaceLayout from './components/WorkspaceLayout';
import { AuthProvider } from './auth/AuthContext';
import { ProfileProvider } from './auth/ProfileContext';
import { useProfile } from './auth/ProfileContext';
import { WorkspaceProvider } from './workspaces/WorkspaceContext';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const PhotographyPage = lazy(() => import('./pages/portfolio/PhotographyPage'));
const VideographyPage = lazy(() => import('./pages/portfolio/VideographyPage'));
const DronePage = lazy(() => import('./pages/portfolio/DronePage'));
const WebDevPage = lazy(() => import('./pages/portfolio/WebDevPage'));
const RealEstatePage = lazy(() => import('./pages/portfolio/RealEstatePage'));
const RealEstateServicePage = lazy(() => import('./pages/services/RealEstateServicePage'));
const PhotographyServicePage = lazy(() => import('./pages/services/PhotographyServicePage'));
const VideographyServicePage = lazy(() => import('./pages/services/VideographyServicePage'));
const DroneServicePage = lazy(() => import('./pages/services/DroneServicePage'));
const WebDevServicePage = lazy(() => import('./pages/services/WebDevServicePage'));
const Platform = lazy(() => import('./pages/Platform'));
const Process = lazy(() => import('./pages/Process'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Book = lazy(() => import('./pages/Book'));
const Contact = lazy(() => import('./pages/Contact'));
const ContactSuccess = lazy(() => import('./pages/ContactSuccess'));
const AboutYounes = lazy(() => import('./pages/AboutYounes'));
const PhotoRequests = lazy(() => import('./pages/admin/PhotoRequests'));
const Card = lazy(() => import('./pages/Card'));
const Login = lazy(() => import('./pages/Login'));
const SignUp = lazy(() => import('./pages/SignUp'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Account = lazy(() => import('./pages/Account'));
const Profile = lazy(() => import('./pages/Profile'));
const Billing = lazy(() => import('./pages/Billing'));
const Properties = lazy(() => import('./pages/Properties'));
const Library = lazy(() => import('./pages/Library'));
const MyGalleries = lazy(() => import('./pages/MyGalleries'));
const Workspace = lazy(() => import('./pages/Workspace'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminOverview = lazy(() => import('./pages/admin/AdminOverview'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminApprovals = lazy(() => import('./pages/admin/AdminApprovals'));
const AdminPlans = lazy(() => import('./pages/admin/AdminPlans'));
const AdminSystem = lazy(() => import('./pages/admin/AdminSystem'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const PublicGallery = lazy(() => import('./pages/PublicGallery'));
const PropertySite = lazy(() => import('./pages/PropertySite'));
const NotFound = lazy(() => import('./pages/NotFound')); // Create a NotFound page
const CommunityApp = lazy(() => import('./community/CommunityApp'));
const KomajPage = lazy(() => import('./komaj/KomajPage'));
const ManageOrderPage = lazy(() => import('./komaj/ManageOrderPage'));
const KomajAdminLayout = lazy(() => import('./pages/admin/komaj/KomajAdminLayout'));
const KomajOverviewPage = lazy(() => import('./pages/admin/komaj/KomajOverview'));
const KomajOrdersPage = lazy(() => import('./pages/admin/komaj/KomajOrders'));
const KomajOrderDetail = lazy(() => import('./pages/admin/komaj/KomajOrderDetail'));
const KomajAvailabilityPage = lazy(() => import('./pages/admin/komaj/KomajAvailability'));
const KomajProductionPage = lazy(() => import('./pages/admin/komaj/KomajProduction'));
const KomajSettingsPage = lazy(() => import('./pages/admin/komaj/KomajSettings'));

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
};

const LegacyProjectRedirect: React.FC = () => {
  const { propertyId } = useParams();
  return <Navigate to={propertyId ? `/galleries/${propertyId}` : '/galleries'} replace />;
};

const WorkspaceHome: React.FC = () => {
  const { profile, loading, error } = useProfile();
  if (profile) return <Navigate to={profile.role === 'client' ? '/my-galleries' : '/galleries'} replace />;
  if (loading) return <Box textAlign="center" py={20}><Spinner size="lg" /></Box>;
  return (
    <VStack py={20} spacing={4} textAlign="center">
      <Text>{error || 'Could not load your account.'}</Text>
      <Button onClick={() => window.location.reload()}>Try again</Button>
    </VStack>
  );
};

// Wraps all routes so we can read location after Router is mounted
const AppInner: React.FC = () => {
  const { pathname } = useLocation();
  const isCard = (pathname === '/card' || pathname === '/card/about');
  const isClientGallery = pathname.startsWith('/gallery/') || pathname.startsWith('/p/');
  const isCommunity = pathname === '/community' || pathname.startsWith('/community/');
  const isKitchen = pathname === '/kitchen' || pathname.startsWith('/kitchen/');
  const hideSiteChrome = isCard || isClientGallery || isCommunity || isKitchen;

  return (
    <>
      <ScrollToTop />
      {!hideSiteChrome && <Header />}
      <main style={{ minHeight: hideSiteChrome ? '100vh' : '80vh' }}>
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/photography" element={<PhotographyPage />} />
            <Route path="/portfolio/videography" element={<VideographyPage />} />
            <Route path="/portfolio/drone" element={<DronePage />} />
            <Route path="/portfolio/web-development" element={<WebDevPage />} />
            <Route path="/portfolio/real-estate" element={<RealEstatePage />} />
            <Route path="/services/real-estate" element={<RealEstateServicePage />} />
            <Route path="/services/business-media" element={<PhotographyServicePage />} />
            <Route path="/services/photography" element={<Navigate to="/services/business-media" replace />} />
            <Route path="/services/videography" element={<VideographyServicePage />} />
            <Route path="/services/drone" element={<DroneServicePage />} />
            <Route path="/services/web-development" element={<WebDevServicePage />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/process" element={<Process />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/book" element={<Book />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/contact/success" element={<ContactSuccess />} />
            <Route path="/card/about" element={<AboutYounes />} />
            <Route path="/card" element={<Card />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/community/*" element={<RequireAuth><CommunityApp /></RequireAuth>} />
            <Route path="/kitchen" element={<KomajPage />} />
            <Route path="/kitchen/order" element={<ManageOrderPage />} />
            {/* Every signed-in page shares one frame and side navigation. */}
            <Route
              element={
                <RequireAuth>
                  <WorkspaceLayout />
                </RequireAuth>
              }
            >
              <Route path="/workspace" element={<WorkspaceHome />} />
              <Route path="/workspaces/:workspaceId" element={<Workspace />} />
              <Route path="/account" element={<Account />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/my-galleries" element={<MyGalleries />} />
              <Route path="/photos/:galleryId" element={<PublicGallery privateDelivery />} />
              <Route path="/galleries" element={<RequireWorkspaceAccess><Properties /></RequireWorkspaceAccess>} />
              <Route path="/galleries/:propertyId" element={<RequireWorkspaceAccess><PropertyDetail /></RequireWorkspaceAccess>} />
              <Route path="/projects" element={<LegacyProjectRedirect />} />
              <Route path="/projects/:propertyId" element={<LegacyProjectRedirect />} />
              <Route path="/properties" element={<LegacyProjectRedirect />} />
              <Route path="/properties/:propertyId" element={<LegacyProjectRedirect />} />
              <Route path="/library" element={<RequireWorkspaceAccess><Library /></RequireWorkspaceAccess>} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverview />} />
                <Route path="photo-requests" element={<PhotoRequests />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="approvals" element={<AdminApprovals />} />
                <Route path="plans" element={<AdminPlans />} />
                <Route path="system" element={<AdminSystem />} />
                <Route path="kitchen" element={<KomajAdminLayout />}>
                  <Route index element={<KomajOverviewPage />} />
                  <Route path="orders" element={<KomajOrdersPage />} />
                  <Route path="orders/:orderNumber" element={<KomajOrderDetail />} />
                  <Route path="availability" element={<KomajAvailabilityPage />} />
                  <Route path="production" element={<KomajProductionPage />} />
                  <Route path="settings" element={<KomajSettingsPage />} />
                </Route>
                <Route path="komaj/*" element={<Navigate to="/admin/kitchen" replace />} />
              </Route>
            </Route>
            <Route path="/gallery/:galleryId" element={<PublicGallery />} />
            <Route path="/p/:slug" element={<PropertySite />} />
            <Route path="*" element={<NotFound />} /> {/* Fallback route */}
          </Routes>
        </Suspense>
      </main>
      {!hideSiteChrome && <Footer />}
      {!isClientGallery && !isKitchen && <AppUpdateNotice />}
    </>
  );
};

const App: React.FC = () => (
  <Router>
    <AuthProvider>
      <ProfileProvider>
        <WorkspaceProvider>
          <AppInner />
        </WorkspaceProvider>
      </ProfileProvider>
    </AuthProvider>
  </Router>
);

export default App;
